import type { CatalogItem, CatalogType, Phase } from '../services/catalog/catalog-types';

/**
 * Base transformer for converting different entity types to CatalogItem
 * Provides shared transformation logic and abstract methods for specific implementations
 */
export abstract class BaseCatalogItemTransformer<T> {
  /**
   * Main transformation method - converts entity to CatalogItem
   */
  toCatalogItem(item: T): CatalogItem {
    return {
      id: this.getId(item),
      slug: this.getSlug(item),
      href: this.getHref(item),
      type: this.getType(item),
      title: this.getTitle(item),
      displayTitle: this.getDisplayTitle(item),
      filename: this.getFilename(item),
      thumbnail: this.getThumbnail(item),
      imageUrl: this.getImageUrl(item),
      width: this.getWidth(item),
      height: this.getHeight(item),
      date: this.getDate(item),
      year: this.getYear(item),
      description: this.getDescription(item),
      longDescription: this.getLongDescription(item),
      medium: this.getMedium(item),
      dimensions: this.getDimensions(item),
      location: this.getLocation(item),
      tags: this.getTags(item),
      detailTags: this.getDetailTags(item),
      // Optional fields
      artist: this.getArtist(item),
      price: this.getPrice(item),
      available: this.getAvailable(item),
      walletAddress: this.getWalletAddress(item),
      duration: this.getDuration(item),
      videoUrl: this.getVideoUrl(item),
      htmlUrl: this.getHtmlUrl(item),
      collections: this.getCollections(item),
      artworkCount: this.getArtworkCount(item),
      collectorCount: this.getCollectorCount(item),
      categories: this.getCategories(item),
      category: this.getCategory(item),
      // Writings specific
      doi: this.getDoi(item),
      authors: this.getAuthors(item),
      publishedIn: this.getPublishedIn(item),
      pdfUrl: this.getPdfUrl(item),
      originalLink: this.getOriginalLink(item),
      // Drop specific
      dropStatus: this.getDropStatus(item),
      dropType: this.getDropType(item),
      publishType: this.getPublishType(item),
      startDate: this.getStartDate(item),
      endDate: this.getEndDate(item),
      revealDateTime: this.getRevealDateTime(item),
      publicPrice: this.getPublicPrice(item),
      totalSupply: this.getTotalSupply(item),
      contractAddress: this.getContractAddress(item),
      phases: this.getPhases(item),
      marketname: this.getMarketname(item),
      marketUrl: this.getMarketUrl(item),
      isDrop: this.getIsDrop(item),
      lastSalePrice: this.getLastSalePrice(item),
      symbol: this.getSymbol(item),
      // Series info
      seriesId: this.getSeriesId(item),
      seriesTitle: this.getSeriesTitle(item),
      seriesSlug: this.getSeriesSlug(item),
      seriesHref: this.getSeriesHref(item),
      seriesYear: this.getSeriesYear(item),
    };
  }

  // Required abstract methods - must be implemented by subclasses
  protected abstract getId(item: T): string | number;
  protected abstract getType(item: T): CatalogType;
  protected abstract getTitle(item: T): string;
  protected abstract getDisplayTitle(item: T): string;
  protected abstract getFilename(item: T): string;
  protected abstract getThumbnail(item: T): string;
  protected abstract getImageUrl(item: T): string;
  protected abstract getWidth(item: T): number;
  protected abstract getHeight(item: T): number;
  protected abstract getTags(item: T): string[];

  // Virtual methods with default implementations - can be overridden
  protected getSlug(_item: T): string | undefined {
    return undefined;
  }

  protected getHref(_item: T): string | undefined {
    return undefined;
  }

  protected getDate(_item: T): string | undefined {
    return undefined;
  }

  protected getYear(_item: T): number | undefined {
    return undefined;
  }

  protected getDescription(_item: T): string | undefined {
    return undefined;
  }

  protected getLongDescription(item: T): string | undefined {
    return this.getDescription(item);
  }

  protected getMedium(_item: T): string | undefined {
    return undefined;
  }

  protected getDimensions(_item: T): string | undefined {
    return undefined;
  }

  protected getLocation(_item: T): string | undefined {
    return undefined;
  }

  protected getDetailTags(_item: T): (string | { title: string; url: string })[] | undefined {
    return undefined;
  }

  protected getArtist(_item: T): string | undefined {
    return undefined;
  }

  protected getPrice(_item: T): number | undefined {
    return undefined;
  }

  protected getAvailable(_item: T): boolean | undefined {
    return undefined;
  }

  protected getWalletAddress(_item: T): string | undefined {
    return undefined;
  }

  protected getDuration(_item: T): string | undefined {
    return undefined;
  }

  protected getVideoUrl(_item: T): string | undefined {
    return undefined;
  }

  protected getHtmlUrl(_item: T): string | undefined {
    return undefined;
  }

  protected getCollections(_item: T): string[] | undefined {
    return undefined;
  }

  protected getArtworkCount(_item: T): number | undefined {
    return undefined;
  }

  protected getCollectorCount(_item: T): number | undefined {
    return undefined;
  }

  protected getCategories(_item: T): string[] | undefined {
    return undefined;
  }

  protected getCategory(_item: T): string | undefined {
    return undefined;
  }

  // Writing specific fields
  protected getDoi(_item: T): string | undefined {
    return undefined;
  }

  protected getAuthors(_item: T): string[] | undefined {
    return undefined;
  }

  protected getPublishedIn(_item: T): string | undefined {
    return undefined;
  }

  protected getPdfUrl(_item: T): string | undefined {
    return undefined;
  }

  protected getOriginalLink(_item: T): string | undefined {
    return undefined;
  }

  // Drop specific fields
  protected getDropStatus(_item: T): 'draft' | 'published' | 'completed' | null | undefined {
    return undefined;
  }

  protected getDropType(_item: T): 'series' | 'oneofone' | null | undefined {
    return undefined;
  }

  protected getPublishType(_item: T): 'hosted' | 'partner' | null | undefined {
    return undefined;
  }

  protected getStartDate(_item: T): string | null | undefined {
    return undefined;
  }

  protected getEndDate(_item: T): string | null | undefined {
    return undefined;
  }

  protected getRevealDateTime(_item: T): string | null | undefined {
    return undefined;
  }

  protected getPublicPrice(_item: T): number | null | undefined {
    return undefined;
  }

  protected getTotalSupply(_item: T): number | null | undefined {
    return undefined;
  }

  protected getContractAddress(_item: T): string | null | undefined {
    return undefined;
  }

  protected getPhases(_item: T): Phase[] | null | undefined {
    return undefined;
  }

  protected getMarketname(_item: T): string | undefined {
    return undefined;
  }

  protected getMarketUrl(_item: T): string | undefined {
    return undefined;
  }

  protected getIsDrop(_item: T): boolean | undefined {
    return undefined;
  }

  protected getLastSalePrice(_item: T): number | undefined {
    return undefined;
  }

  protected getSymbol(_item: T): string | undefined {
    return undefined;
  }

  // Series info methods
  protected getSeriesId(_item: T): number | null | undefined {
    return undefined;
  }

  protected getSeriesTitle(_item: T): string | null | undefined {
    return undefined;
  }

  protected getSeriesSlug(_item: T): string | null | undefined {
    return undefined;
  }

  protected getSeriesHref(_item: T): string | null | undefined {
    return undefined;
  }

  protected getSeriesYear(_item: T): number | null | undefined {
    return undefined;
  }
}